/**
 * Single source of truth for the AI search / assistant analytics.
 *
 * Every AI event (home + SRP) is routed through the existing CleverTap hooks
 * (`useClevertapForHomepage` / `useClevertapForSrp`) which already forward to
 * NewRelic and auto-inject page / locale / user context. We only define the
 * AI-specific event names + payload keys here so the data stays consistent and
 * easy to report on for management.
 */

export const AI_EVENT_NAME = {
    PAGE_LOAD: 'Air_PageLoad',
    UI_ACTION: 'Air_UI_Action'
} as const

/** `action_name` values — one per step of the AI funnel. */
export const AI_ACTION = {
    /** Home: user switches the toggle from normal search into AI mode. */
    ENTER_AI_MODE: 'navigate_to_ai_mode',
    /** Home: user switches the toggle back from AI mode into normal search. */
    EXIT_AI_MODE: 'navigate_to_search_mode',
    /** Home: user sent a query and the AI responded (intent / clarification / ready). */
    HOME_QUERY: 'ai_home_query',
    /** Home: user confirmed and is being handed off to the SRP with the AI query. */
    HOME_CONTINUE_TO_SRP: 'ai_home_continue_to_srp',
    /** SRP: AI panel auto-opened because the user arrived with `search_with_ai=true`. */
    SRP_PAGE_LOAD: 'ai_srp_page_load',
    /** SRP: user manually opened the AI chat from the toggle. */
    SRP_OPEN_CHAT: 'ai_srp_open_chat',
    /** SRP: user sent a query in the AI chat and the AI took an action. */
    SRP_QUERY: 'ai_srp_query'
} as const

export type AIActionName = (typeof AI_ACTION)[keyof typeof AI_ACTION]

export interface AIEventInput {
    /** Funnel step — use a value from `AI_ACTION`. */
    action_name: AIActionName
    /** High-level interaction bucket, e.g. 'mode_toggle' | 'query' | 'navigation' | 'page_load'. */
    action_type?: string
    /** Short machine value for the action (mode value, 'open', etc.). */
    action_value?: string
    /** Whether AI mode is active for this interaction. Defaults to `true`. */
    aimode?: boolean
    /** Raw natural-language query the user typed. */
    ai_user_query?: string
    /** What the AI decided / its status, e.g. 'ready' | 'needs_clarification' | 'RECOMMENDATION_RESULT'. */
    ai_response_status?: string
    /** Assistant's textual reply / follow-up question, when present. */
    ai_response?: string
    /** Any extra AI dimensions (ai_route, ai_filter_query, ai_layout, …). */
    [key: string]: string | number | boolean | undefined
}

/** Free-text fields (user query / AI reply) are trimmed so payloads stay light. */
const MAX_TEXT_LENGTH = 300

const truncate = (value?: string) =>
    typeof value === 'string' && value.length > MAX_TEXT_LENGTH ? value.slice(0, MAX_TEXT_LENGTH) : value

/**
 * Normalises an AI interaction into a flat, CleverTap/NewRelic-friendly payload:
 * defaults `aimode` + `action_type`, trims free-text fields and drops `undefined`s.
 */
export const buildAIEventPayload = (input: AIEventInput): Record<string, string | number | boolean> => {
    const { aimode = true, action_type = 'ai_mode', ai_user_query, ai_response, ...rest } = input

    const payload: Record<string, string | number | boolean> = {
        aimode,
        action_type
    }

    if (ai_user_query !== undefined) payload.ai_user_query = truncate(ai_user_query) ?? ''
    if (ai_response !== undefined) payload.ai_response = truncate(ai_response) ?? ''

    Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined) payload[key] = value
    })

    return payload
}
